import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { TrackerRect } from './barcode-tracker-selector';
import {
  DETECTED_STROKE_COLOR,
  IDLE_SIDE_FRACTION,
  IDLE_STROKE_COLOR,
  MORPH_DURATION_MS,
  STROKE_WIDTH_PX,
} from './barcode-tracker-constants';
import { buildBracketPath } from './bracket-path-builder';

interface PendingMorph {
  fromRect: TrackerRect;
  toRect: TrackerRect;
  startTime: number;
  durationMs: number;
  onComplete?: () => void;
  rafId: number;
}

/** Exact equality on rect scalars — only used for `resync` idempotency,
 *  not for animation interpolation. The overlay always reads its own
 *  computed rects, so two equal rects produce identical SVG path `d`
 *  strings; comparing scalars exactly is sufficient. */
function rectsEqual(a: TrackerRect, b: TrackerRect): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/** SVG bracket-tracker overlay: an idle white-bracket centered square that
 *  morphs onto a detected barcode's bounding rect (in video-element DOM
 *  space) with a 120ms ease-out path morph and a lockstep stroke
 *  cross-fade to `$yellow-300`.
 *
 *  Two animation primitives: a single `requestAnimationFrame` loop drives
 *  the `<path>` `d` attribute (cross-browser uniform — WAAPI animation of
 *  `d` is Chromium-only), and the Web Animations API drives the stroke
 *  color cross-fade. The stroke is written directly to the path element's
 *  inline `style.stroke` (NOT a `[style.stroke]` Angular binding — that
 *  would race with WAAPI and be overwritten on every CD pass). WAAPI on
 *  the inline `stroke` CSS property animates reliably across Chromium +
 *  WebKit + Firefox.
 *
 *  The first-morph completion gate lives on the host (`cs-camera-view`),
 *  not here — this component just exposes the contract: pass a
 *  `onMorphComplete` callback into `setDetected` and we'll fire it when
 *  the rAF loop reaches t≥1 naturally. Cancellation does not fire the
 *  callback (mirrors iOS `AnimationCompletionDelegate`).
 */
@Component({
  selector: 'cs-barcode-tracker-overlay',
  templateUrl: 'cs-barcode-tracker-overlay.html',
  styleUrls: ['cs-barcode-tracker-overlay.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsBarcodeTrackerOverlayComponent implements AfterViewInit, OnDestroy {
  @ViewChild('svgEl', { static: true }) svgRef!: ElementRef<SVGSVGElement>;
  @ViewChild('pathEl', { static: true }) pathRef!: ElementRef<SVGPathElement>;

  // expose to template binding
  readonly STROKE_WIDTH_PX = STROKE_WIDTH_PX;

  pathD = '';

  private currentRect: TrackerRect | null = null;
  // current stroke value for read-back when starting a cross-fade. Written
  // directly to the path element's inline style by snapTo/morphTo (NOT via
  // an Angular binding — see class-level comment for why).
  private currentStroke = IDLE_STROKE_COLOR;
  private pending: PendingMorph | null = null;
  private strokeAnim: Animation | null = null;
  private destroyed = false;

  // ResizeObserver on the host so a viewport rotate / resize re-runs the
  // idle pose against the new SVG dimensions (otherwise the path `d`
  // would still encode old clientWidth/clientHeight).
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private cd: ChangeDetectorRef,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  ngAfterViewInit(): void {
    // Paint the initial stroke directly so the path starts visible even
    // before the first morph (CD has already committed pathD='', but no
    // stroke means the path is invisible). Apply the inline style now.
    this.applyStroke(IDLE_STROKE_COLOR);

    // for window/device rotation + container resize: re-flow the idle pose
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.destroyed) return;
        this.resync();
      });
      this.resizeObserver.observe(this.hostRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelPending();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  /** Snap or animate to the centered idle square. `animated=false` paints
   *  the pose immediately (used at startup and on teardown). */
  setIdle(animated: boolean): void {
    if (this.destroyed) return;
    const target = this.computeIdleRect();
    if (!target) {
      // nothing to draw against yet — wait for video dims to come in
      return;
    }
    if (!animated) {
      this.snapTo(target, IDLE_STROKE_COLOR);
      return;
    }
    this.morphTo(target, IDLE_STROKE_COLOR);
  }

  /** Morph the brackets onto the given rect (in video-element DOM space).
   *  Stroke cross-fades to `$yellow-300` in lockstep.
   *
   *  `onMorphComplete` (when provided) fires exactly when the rAF loop
   *  reaches t≥1 naturally. A new `setIdle`/`setDetected` before the
   *  in-flight morph completes cancels it, drops the prior callback, and
   *  starts a fresh morph from the currently-painted pose — the prior
   *  `onMorphComplete` is never fired. */
  setDetected(rect: TrackerRect, onMorphComplete?: () => void): void {
    if (this.destroyed) return;
    if (!rect || rect.w <= 0 || rect.h <= 0) {
      // degenerate guard — fire completion immediately, don't paint a
      // zero-size rect (matches iOS guard).
      onMorphComplete?.();
      return;
    }
    this.morphTo(rect, DETECTED_STROKE_COLOR, onMorphComplete);
  }

  /** Re-paint the current pose against (possibly changed) host dimensions.
   *  Called from the ResizeObserver whenever the host changes size
   *  (orientation flip, viewport resize, native frame retracking).
   *  Re-flows only the idle pose; an in-flight or finished detected
   *  morph keeps its DOM-pixel coords (the host re-emits a detected
   *  rect when Quagga reports a new frame).
   *
   *  Idempotent — when called with the same dims it currently shows,
   *  this returns without touching the DOM. That deduplicates the
   *  double-paint that would otherwise happen on first init: the
   *  ResizeObserver schedules a synthetic initial callback that fires
   *  alongside the host's deferred `setIdle(false)`, so both arrive
   *  with the same idle rect; whichever runs first wins, the other
   *  no-ops on the dim-equality check. */
  resync(): void {
    if (this.destroyed || this.pending) return;
    if (this.currentStroke !== IDLE_STROKE_COLOR) return;
    const target = this.computeIdleRect();
    if (!target) return;
    if (this.currentRect && rectsEqual(this.currentRect, target)) return;
    this.snapTo(target, IDLE_STROKE_COLOR);
  }

  // ── Internal animation primitives ──────────────────────────────────

  private snapTo(rect: TrackerRect, strokeColor: string): void {
    this.cancelPending();
    this.currentRect = rect;
    this.applyStroke(strokeColor);
    this.pathD = buildBracketPath(rect);
    if (!this.destroyed) this.cd.markForCheck();
  }

  private morphTo(rect: TrackerRect, strokeColor: string, onComplete?: () => void): void {
    const fromRect = this.currentRect ?? rect;
    const fromColor = this.currentStroke;
    this.cancelPending();

    // 1) rAF-driven path morph
    const startTime = performance.now();
    const durationMs = MORPH_DURATION_MS;
    const tick = () => {
      if (!this.pending || this.destroyed) return;
      const now = performance.now();
      const raw = (now - startTime) / durationMs;
      const t = raw >= 1 ? 1 : raw <= 0 ? 0 : raw;
      // quadratic ease-out: t' = 1 - (1 - t)^2
      const e = 1 - (1 - t) * (1 - t);
      const interp: TrackerRect = {
        x: fromRect.x + (rect.x - fromRect.x) * e,
        y: fromRect.y + (rect.y - fromRect.y) * e,
        w: fromRect.w + (rect.w - fromRect.w) * e,
        h: fromRect.h + (rect.h - fromRect.h) * e,
      };
      this.currentRect = interp;
      this.pathD = buildBracketPath(interp);
      if (!this.destroyed) this.cd.markForCheck();
      if (t >= 1) {
        const cb = this.pending.onComplete;
        this.pending = null;
        cb?.();
        return;
      }
      this.pending.rafId = requestAnimationFrame(tick);
    };

    this.pending = {
      fromRect,
      toRect: rect,
      startTime,
      durationMs,
      onComplete,
      rafId: 0,
    };
    this.pending.rafId = requestAnimationFrame(tick);

    // 2) WAAPI stroke color cross-fade (locked to MORPH_DURATION_MS so
    // it lands with the path morph). Drive WAAPI against the inline
    // `style.stroke` CSS property — that's the only path that animates
    // reliably across Chromium + WebKit + Firefox. The final color is
    // also written to inline style synchronously below so the post-
    // animation steady state survives a CD pass.
    this.applyStroke(strokeColor);
    const pathEl = this.pathRef?.nativeElement;
    if (pathEl && typeof pathEl.animate === 'function') {
      try {
        this.strokeAnim = pathEl.animate(
          [{ stroke: fromColor }, { stroke: strokeColor }],
          { duration: MORPH_DURATION_MS, easing: 'ease-out', fill: 'forwards' },
        );
      } catch (err) {
        // WAAPI not available or rejected — the inline-style write above
        // means the final color still paints correctly; we just lose the
        // cross-fade animation.
        console.log('cs-barcode-tracker-overlay: WAAPI stroke animate rejected:', err);
        this.strokeAnim = null;
      }
    }
    if (!this.destroyed) this.cd.markForCheck();
  }

  /** Write the stroke value to the path's inline style and remember it
   *  for the next morph's `fromColor`. WAAPI animations on `stroke`
   *  override this for their duration; outside an animation the inline
   *  style is the steady state. */
  private applyStroke(color: string): void {
    this.currentStroke = color;
    const pathEl = this.pathRef?.nativeElement;
    if (pathEl) pathEl.style.stroke = color;
  }

  private cancelPending(): void {
    if (this.pending) {
      cancelAnimationFrame(this.pending.rafId);
      // suppress completion callback for cancelled animations
      this.pending.onComplete = undefined;
      this.pending = null;
    }
    if (this.strokeAnim) {
      try {
        this.strokeAnim.cancel();
      } catch (err) {
        console.log('cs-barcode-tracker-overlay: stroke animation cancel rejected:', err);
      }
      this.strokeAnim = null;
    }
  }

  private computeIdleRect(): TrackerRect | null {
    // Read the SVG's own client rect — it's positioned with `inset: 0`
    // inside the camera-container, so its dims match the video's
    // rendered box (which is what we care about for centering).
    const svg = this.svgRef?.nativeElement;
    if (!svg) return null;
    const w = svg.clientWidth;
    const h = svg.clientHeight;
    if (w <= 0 || h <= 0) return null;
    const side = Math.min(w, h) * IDLE_SIDE_FRACTION;
    return {
      x: (w - side) / 2,
      y: (h - side) / 2,
      w: side,
      h: side,
    };
  }
}
